import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { types as coreTypes } from '@sitebuilder/core';
import { hooks as commonHooks } from '@sitebuilder/common';
import { formResourceIdRegex } from '../../../utils';
import {
    ErrorMessage,
    PrimaryButton,
    SideBarAccordion,
    SpinnerIcon,
    SuccessMessage,
    TextInput,
} from '..';
import { StateShape as ReduxStateShape } from '../../../reducers';
import { actionCreators } from '../../../reducers/resources';

const idRegex = formResourceIdRegex;

type FormData = any;

export interface AddNewResourceFormProps {
    resources: coreTypes.Resource | null;
    filteredResources?: coreTypes.Resource | null;
    resourcesType: string;
}

const AddNewResourceForm = (props: AddNewResourceFormProps) => {
    const { filteredResources, resourcesType } = props;
    const dispatch = useDispatch();
    const i18n = commonHooks.useLocalization();

    const locales: string[] = filteredResources ? Object.keys(filteredResources) : [];

    // initial form data
    const initialData: any = {
        id: '',
    };

    // Assign empty values to every locale by default
    locales?.forEach((locale: string) => (initialData[locale] = ''));

    const [data, setData] = React.useState<FormData>(initialData);

    // error messages for invalid inputs
    const [dataError, setDataError] = React.useState<FormData>(initialData);

    // error message on locale save
    const [error, setError] = React.useState<string>('');

    // success message on locale save
    const [success, setSuccess] = React.useState<string>('');

    const { resources, resourcesSaveLoading, resourcesSaveSuccess, resourcesSaveError } =
        useSelector((state: ReduxStateShape) => ({
            ...state?.resources,
        }));

    useEffect(() => {
        if (resourcesSaveSuccess) {
            setSuccess(resourcesSaveSuccess);

            if (resourcesType === 'site') {
                dispatch(actionCreators.getResources());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesSaveSuccess]);

    useEffect(() => {
        if (resourcesSaveError) {
            setError(i18n.t('new_resource_save_error'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesSaveError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
        const { value } = e?.target;

        if (error) {
            setError('');
        }

        if (success) {
            setSuccess('');
        }

        if (!value && dataError[field]) {
            setDataError({ ...dataError, [field]: '' });
        }

        if (field === 'id') {
            if (value.trim()) {
                if (idRegex.test(value)) {
                    setDataError({ ...dataError, id: i18n.t('form_invalid_name') });
                } else {
                    setDataError({ ...dataError, id: '' });
                }
            } else {
                setDataError({ ...dataError, id: i18n.t('form_missing_id') });
            }
        }

        setData({
            ...data,
            [field]: value,
        });
    };

    const validateFormData = () => {
        const { id } = data;
        const err: FormData = {
            id: '',
        };

        if (!id) {
            err.id = i18n.t('form_missing_id');
            setDataError({ ...dataError, ...err });
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        const isFormValid = validateFormData();

        if (isFormValid) {
            const refResources =
                resourcesType === 'site' ? { ...resources } : { ...filteredResources };
            // check if same id exists or not
            const ifIdExists =
                refResources?.['en-US'] && Object.keys(refResources['en-US']).includes(data.id);

            if (ifIdExists) {
                setError(i18n.t('id_already_exists'));
                setSuccess('');
            } else {
                const newResources: any =
                    resourcesType === 'site' ? { ...resources } : { ...filteredResources };
                Object.keys(data)
                    ?.filter((key: string) => key !== 'id')
                    ?.forEach((k: string) => {
                        newResources[k] = { ...newResources?.[k], [data.id]: data[k] };
                    });

                await dispatch(actionCreators.saveResources(newResources, resourcesType, false));
                setData(initialData);
                setDataError(initialData);
            }
        }
    };

    const [accordionActiveKeys, setAccordionActiveKeys] = useState(['0']);

    const onClickAccordion = (eventKey: string) => {
        if (accordionActiveKeys.includes(eventKey)) {
            setAccordionActiveKeys(accordionActiveKeys.filter((k) => k !== eventKey));
        } else if (eventKey) {
            setAccordionActiveKeys([eventKey]);
        }
    };

    return (
        <>
            <hr className="divider" />
            <SideBarAccordion
                toggleTitle={'Create New Resource'}
                eventKey="0"
                defaultActiveKey="0"
                accordionActiveKey={accordionActiveKeys}
                onClickAccordion={onClickAccordion}
            >
                <div className="save-form">
                    <TextInput
                        className={'form-input'}
                        label={i18n.t('form_id')}
                        inputId={'id-input'}
                        labelId={'id-label'}
                        placeholder={i18n.t('form_id')}
                        value={data.id}
                        onChange={(e) => handleInputChange(e, 'id')}
                        error={dataError.id}
                    />
                    {locales?.map((locale: string, index: number) => (
                        <TextInput
                            key={`${locale}${index}`}
                            className={'form-input'}
                            label={locale}
                            inputId={`${locale}-input`}
                            labelId={`${locale}-label`}
                            placeholder={locale}
                            value={data[locale]}
                            onChange={(e) => handleInputChange(e, locale)}
                        />
                    ))}
                    <div className="save">
                        {error && <ErrorMessage error={error} />}
                        {success && <SuccessMessage message={success} />}
                        <PrimaryButton
                            id={'resource-save-button'}
                            className={'save-button'}
                            onClick={handleSubmit}
                        >
                            {resourcesSaveLoading && <SpinnerIcon />}
                            {i18n.t('save')}
                        </PrimaryButton>
                    </div>
                </div>
            </SideBarAccordion>
        </>
    );
};

export default AddNewResourceForm;
